import CloseIcon from '@mui/icons-material/Close'
import {
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    Stack,
    Typography
} from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import MeetJolt from '@/features/account/first-run/MeetJolt'
import { isPlayer } from '@/features/dashboard/home/playerHome'
import { showSetupNudge } from '@/features/dashboard/setup/stepDisplay'
import { isFeatureFlagEnabled } from '@/features/dashboard/site-admin/orgFeatureFlags'
import useAuth from '@/hooks/useAuth'
import { useFullScreenDialog } from '@/hooks/useFullScreenDialog'
import useGamey from '@/hooks/useGamey'
import useTerminology from '@/hooks/useTerminology'
import { ORG_SETUP_ROLES } from '@/lib/roles'
import { hasRole } from '@/utils/hasRole'

/**
 * The first thing a new user sees.
 *
 * It used to be three compulsory steps (Welcome, Profile Picture, Appearance)
 * with no way out: `disableEscapeKeyDown`, no `onClose`, and a "Skip for now"
 * on the avatar step only. So a school's energy manager met this product
 * for the first time through a form about their photo and a choice of six
 * themes and eight accent colours, and could not leave until they had walked
 * to the end of it.
 *
 * Neither choice needed to be here: the theme lives in the settings drawer on
 * the account menu, and the avatar in Account settings. Both were reachable
 * before this dialog existed and still are, so this now does the one thing
 * only a welcome can do (say what the product is for, and point at the single
 * next action worth taking) and gets out of the way on any dismissal.
 */
const OnboardingDialog = () => {
    const fullScreen = useFullScreenDialog()
    const {
        activeOrg,
        isAuthenticated,
        refreshSession,
        settings,
        updateSettings,
        user
    } = useAuth()
    const { t } = useTerminology()
    const gamey = useGamey()
    const [saving, setSaving] = useState(false)

    const onboardingCompleted = settings?.onboarding?.completed === true
    const open = isAuthenticated && !onboardingCompleted

    // Dismissing is completing. A welcome that reappears every session is a
    // nag, and everything it offers is reachable from the page behind it.
    const handleComplete = async () => {
        setSaving(true)
        try {
            await refreshSession()
            updateSettings({
                model: 'user',
                attribute: ['onboarding'],
                value: { completed: true }
            })
        } finally {
            setSaving(false)
        }
    }

    if (!open) {
        return null
    }

    // A player on joulelingo.com meets Jolt instead, and the same flag marks it
    // done.
    if (gamey && isPlayer(user)) {
        return (
            <MeetJolt
                quizOn={isFeatureFlagEnabled(activeOrg, 'energy_quiz')}
                saving={saving}
                onDone={handleComplete}
            />
        )
    }

    // An admin whose org is half-configured has a different first move from
    // everyone else: there is nothing to look at until setup is further along.
    const canRunSetup = Boolean(user) && hasRole(user, ORG_SETUP_ROLES)
    const needsSetup = showSetupNudge(activeOrg, canRunSetup)
    const action = needsSetup
        ? { label: 'Finish setting up', to: '/setup' }
        : { label: 'See your energy', to: '/power' }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleComplete}
            maxWidth="sm"
            fullWidth
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(4px)' } } }}
        >
            <DialogContent sx={{ p: { xs: 2.5, sm: 4 } }}>
                <Stack spacing={2.5}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Typography variant="h5">Welcome to Power</Typography>
                        <IconButton
                            aria-label="Close"
                            onClick={handleComplete}
                            size="small"
                            sx={{ mt: -1, mr: -1 }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Stack>

                    <Typography color="text.secondary">
                        Power turns your meter readings into the figures,
                        targets and alerts your {t('orgNoun')} can act on.
                    </Typography>

                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <Button
                            component={RouterLink}
                            to={action.to}
                            variant="contained"
                            onClick={handleComplete}
                            disabled={saving}
                            startIcon={
                                saving ? <CircularProgress size={16} /> : null
                            }
                        >
                            {action.label}
                        </Button>
                        <Button
                            color="inherit"
                            onClick={handleComplete}
                            disabled={saving}
                        >
                            Have a look around
                        </Button>
                    </Stack>

                    <Typography variant="caption" color="text.secondary">
                        You can add a profile picture and change the theme any
                        time from the account menu.
                    </Typography>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}

export default OnboardingDialog
